/**
 * `<StatusEditor>` — the shared host-agnostic **status UI**: one chip per active status (each with
 * a remove `×`) plus an add form. It owns only the draft text (+ the optional source/partner
 * picks), taking the combatant + `actorId` + a `dispatch` as props and building its commands
 * through the pure {@link applyStatusCommand}/{@link removeStatusCommand} model. Knows nothing
 * about the room — the host wires `dispatch` (the editor-kit discipline: a control takes
 * `dispatch`/`actorId` as props and never hardcodes them).
 *
 * **The source picker** is the browser's only way to set `apply_status`'s optional `source`.
 * Without it a GM could apply "Exemplar's Mark", see it render on the token, and the reaction it
 * exists to enable (Valiant Aid) would silently never fire. Same in-panel `<select>` idiom as
 * {@link SearchControls} (`CombatWindows.tsx`) and {@link BaseEditPanel}'s weapon/mech pickers — a
 * `cw-field` select, not a board-click gesture, since there's no reach-wash to hang a click on.
 * Left at its default `""` (no source), so every status that isn't a third-party mark (IMPAIRED,
 * JAMMED, …) needs no new input.
 *
 * **The partner picker** is likewise the browser's only way to set the optional `partner` — what
 * stands up a "Bondmate" marker so `t_bonded:1`'s adjacency-gated save/check bonus has a partner to
 * read when the roster names no Bondmate (a deploy plants the roster pair itself). Same idiom,
 * defaulting to `""` (no partner).
 *
 * **The expiry picker** sets the optional `expiry`: `default` (omitted, so the reducer applies the
 * status's RAW duration or `until_removed`), `until_removed`, or `save_ends` with an optional HASE
 * `stat` axis for the re-save — the browser twin of `status add --expiry save_ends --stat <axis>`.
 * The axis select renders only while `save_ends` is picked.
 *
 * **The name field suggests the status catalogue** ({@link STATUS_BLURBS}'s names) through a
 * `<datalist>`, and still takes any free text, so a homebrew status needs no catalogue row.
 *
 * The add form is its own export, {@link StatusAddForm}, because the PLAY unit sheet's statuses
 * row (`UnitSheetStatuses`) lays out its chips its own way and mounts the same form beneath them for
 * the GM.
 */
import { useId, useState } from "react";
import type { Combatant, CombatantId, EncounterCommand, EncounterState } from "@lancer-vtt/domain";
import type { Result } from "@lancer-vtt/shared";
import { applyStatusCommand, decrementRulingCommand, expiryFromPick, removeStatusCommand, SAVE_STATS, type ExpiryPick } from "./status";
import { StatusChip } from "./StatusChip";
import { STATUS_BLURBS } from "./statusCatalog";
import { tipAttrs } from "../tooltip";
import { Icon } from "../Icon";
import { faPlus } from "@fortawesome/free-solid-svg-icons";

/** The catalogue's status names, alphabetical — the add field's suggestions. */
const CATALOGUE_NAMES = Object.keys(STATUS_BLURBS).sort((a, b) => a.localeCompare(b));

/** The host's encounter dispatch — applies a command and reports the reducer's accept/reject. */
export type EncounterDispatch = (command: EncounterCommand) => Result<void>;

export function StatusEditor({
  combatant: c,
  state,
  actorId,
  dispatch,
  canRule = false,
}: {
  combatant: Combatant;
  /** The live encounter state — read only to list source/partner candidates (every OTHER combatant). */
  state: EncounterState;
  actorId: string | null;
  dispatch: EncounterDispatch;
  /** The viewer is the GM: a stacked ruling's chip gets the `−` (a GM-only `grant_ruling`). */
  canRule?: boolean;
}) {
  return (
    <div className="cw-status">
      <span className="cw-microlabel">Statuses</span>
      {c.statuses.map((s) => (
        <StatusChip
          key={s.name}
          status={s}
          className="cw-status-chip"
          onRemove={
            actorId
              ? () => dispatch(removeStatusCommand({ actorId, combatantId: c.id, status: s.name }))
              : undefined
          }
          onDecrement={
            actorId && canRule
              ? () => {
                  const command = decrementRulingCommand({ actorId, combatantId: c.id, status: s });
                  if (command !== undefined) dispatch(command);
                }
              : undefined
          }
        />
      ))}
      <StatusAddForm combatantId={c.id} state={state} actorId={actorId} dispatch={dispatch} />
    </div>
  );
}

/** The add half of {@link StatusEditor}: the name field plus the source, partner and expiry picks. */
export function StatusAddForm({
  combatantId,
  state,
  actorId,
  dispatch,
}: {
  combatantId: CombatantId;
  state: EncounterState;
  actorId: string | null;
  dispatch: EncounterDispatch;
}) {
  const catalogueId = useId();
  const [draft, setDraft] = useState("");
  const [source, setSource] = useState<CombatantId | "">("");
  const [partner, setPartner] = useState<CombatantId | "">("");
  const [expiryPick, setExpiryPick] = useState<ExpiryPick>("default");
  const [saveStat, setSaveStat] = useState("");

  const otherCombatants = state.order
    .map((id) => state.combatants[id])
    .filter((t): t is Combatant => !!t && t.id !== combatantId);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!actorId) return;
        const expiry = expiryFromPick(expiryPick, saveStat);
        const built = applyStatusCommand({
          actorId,
          combatantId,
          status: draft,
          ...(source !== "" ? { source } : {}),
          ...(partner !== "" ? { partner } : {}),
          ...(expiry !== undefined ? { expiry } : {}),
        });
        if (!built.ok) return;
        const res = dispatch(built.value);
        if (res.ok) {
          setDraft("");
          setSource("");
          setPartner("");
          setExpiryPick("default");
          setSaveStat("");
        }
      }}
      className="cw-status-form"
    >
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="add status…"
        aria-label="status name"
        list={catalogueId}
        className="cw-field is-status"
      />
      <datalist id={catalogueId}>
        {CATALOGUE_NAMES.map((name) => (
          <option key={name} value={name} />
        ))}
      </datalist>
      <select
        aria-label="status source"
        value={source}
        onChange={(e) => setSource(e.target.value as CombatantId | "")}
        className="cw-field is-w14"
      >
        <option value="">source (optional)…</option>
        {otherCombatants.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </select>
      <select
        aria-label="status partner"
        value={partner}
        onChange={(e) => setPartner(e.target.value as CombatantId | "")}
        className="cw-field is-w14"
      >
        <option value="">partner (optional)…</option>
        {otherCombatants.map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
          </option>
        ))}
      </select>
      <select
        aria-label="status expiry"
        value={expiryPick}
        onChange={(e) => setExpiryPick(e.target.value as ExpiryPick)}
        className="cw-field is-w14"
      >
        <option value="default">expiry: default</option>
        <option value="until_removed">until removed</option>
        <option value="save_ends">save ends</option>
      </select>
      {expiryPick === "save_ends" && (
        <select
          aria-label="save stat"
          value={saveStat}
          onChange={(e) => setSaveStat(e.target.value)}
          className="cw-field is-w14"
        >
          <option value="">save stat (none)…</option>
          {SAVE_STATS.map((stat) => (
            <option key={stat} value={stat}>
              {stat}
            </option>
          ))}
        </select>
      )}
      <button type="submit" disabled={!draft.trim() || !actorId} className="cw-btn" aria-label="Add status" {...tipAttrs({ label: "Add status" })}>
        <Icon icon={faPlus} />
      </button>
    </form>
  );
}
